import React from 'react';
import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { ImageField, SelectField } from './MenuUI';
import { previewUrl, uploadMenuImage } from './uploadMenuImage';

/**
 * One dialog for adding a menu item and for correcting one.
 *
 * The phone's two sheets are the same three fields in the same order — name,
 * price, category — with a picture tile beside the category. Same here, with
 * the tile given its own row because a desktop dialog has the width and the
 * picture is the part people came to change.
 *
 * The wording is the phone's: `Geli Magaca`, `Geli Qiimaha Iibsiga`, `Category`,
 * `Ku Dar` to add and `Update Now` to save. Nothing was invented in Somali.
 */

const COPY = {
  addTitle: 'New Menu Item',
  editTitle: 'Edit Menu Item',
  name: 'Geli Magaca',
  price: 'Geli Qiimaha Iibsiga',
  category: 'Category',
  noCategory: 'No category',
  add: 'Ku Dar',
  save: 'Update Now',
  // The phone's own validation messages, from add_menu_item_cubit.dart.
  nameRequired: 'Fadlan Geli Magaca Alaabta!',
  priceRequired: 'Fadlan Geli Qiimaha Alaabta!',
  failed: 'Could not save. Try again.',
  // Kept out of the markup: an apostrophe loose in JSX text reads as a quote
  // to most tooling, and has broken this build once already.
  printerNote: "The category decides which printer prints this item's ticket.",
};

export default function MenuItemDialog({ item, categories, onClose, onSubmit }) {
  const editing = Boolean(item);

  const [name, setName] = React.useState(() => (item && item.name) || '');
  const [price, setPrice] = React.useState(() =>
    item && item.price !== undefined && item.price !== null ? String(item.price) : ''
  );
  const [category, setCategory] = React.useState(() => {
    const value = item && item.category;
    if (!value) return '';
    return typeof value === 'object' ? value._id || '' : value;
  });

  // What the item will carry: the URL already on it, or the one storage gave
  // back for a newly chosen file. `preview` is the local file while it uploads,
  // so the tile fills the moment something is picked.
  const [imageUrl, setImageUrl] = React.useState(() => (item && item.imageUrl) || '');
  const [preview, setPreview] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  // Revoke the object URL when the dialog closes, or the blob is held for the
  // life of the page.
  React.useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const pickImage = async (file) => {
    setError('');
    setProgress(0);
    setUploading(true);

    const local = previewUrl(file);
    setPreview(local);

    try {
      const url = await uploadMenuImage(file, { onProgress: setProgress });
      setImageUrl(url);
    } catch (err) {
      setPreview('');
      URL.revokeObjectURL(local);
      setError(err.message || 'Could not upload the image');
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview('');
    setImageUrl('');
  };

  const submit = async () => {
    if (busy || uploading) return;

    if (!name.trim()) {
      setError(COPY.nameRequired);
      return;
    }

    const value = Number(price);
    if (!String(price).trim() || Number.isNaN(value) || value <= 0) {
      setError(COPY.priceRequired);
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        item,
        name: name.trim(),
        price: value,
        // '' means "no category", which the server stores as no category at
        // all rather than an empty string it would then fail to cast.
        category: category || null,
        // Sent even when empty: clearing the picture has to reach the server.
        imageUrl: imageUrl || '',
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editTitle : COPY.addTitle}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 18 }}>
          <PillField
            name="menu-item-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.name}
            disabled={busy}
            autoFocus
          />

          <PillField
            name="menu-item-price"
            type="number"
            value={price}
            onChange={(value) => {
              setPrice(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.price}
            disabled={busy}
          />

          <SelectField
            name="menu-item-category"
            label={COPY.category}
            value={category}
            onChange={setCategory}
            disabled={busy}
          >
            <option value="">{COPY.noCategory}</option>
            {categories.map((entry) => (
              <option key={entry._id} value={entry._id}>
                {entry.name}
              </option>
            ))}
          </SelectField>

          <ImageField
            src={preview || imageUrl}
            busy={uploading}
            progress={progress}
            disabled={busy}
            onPick={pickImage}
            onClear={clearImage}
          />
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          disabled={uploading}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.save : COPY.add}
        </PrimaryButton>

        {/* The category a dish sits in decides which printer prints its
            ticket, which is a thing worth knowing while choosing one. */}
        <div
          style={{
            fontSize: 11.5,
            color: T.inkFaint,
            lineHeight: 1.5,
            textAlign: 'center',
            paddingTop: 14,
          }}
        >
          {COPY.printerNote}
        </div>
      </div>
    </Dialog>
  );
}
